import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, FormControl, FormLabel, Input, Text, Heading, Button, useToast, useColorModeValue,
  Flex, VStack, HStack, Select, Textarea, Radio, RadioGroup, Stack, Table, Thead, Tbody, Tr, Th, Td, Divider, Badge,
} from "@chakra-ui/react";
import { fmtLocal, utcToInputValue, inputValueToUtc, parseUtc, asUtcDate, toBackendISO } from "../../utils/dates";
import axios from "axios";

interface Coupon {
  coupon_id: number;
  coupon_code: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  start_date?: string;
  end_date?: string;
  max_redemptions?: number;
  redemption_count: number;
  is_active: boolean;
  applicable_offer_id?: number;
  batch_label?: string;
  created_at: string;
}

interface Redemption {
  coupon_redemption_id: number;
  purchase_id: number;
  account_id: number;
  discount_amount: number;
  redeemed_at: string;
}

const B2CReviewCoupon = () => {
  const cardbg = useColorModeValue("gray.50", "gray.700");
  const { coupon_id } = useParams<{ coupon_id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const pageBg = useColorModeValue("gray.100", "gray.700");
  const couponBg = useColorModeValue("gray.100", "gray.700");
  const couponCodeBg = useColorModeValue("white", "gray.800");


  const isCreating = Number(coupon_id) === 0;
  const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
  const token = localStorage.getItem("b2c_token");

  // ── View mode (existing coupon) ──
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);

  const fetchCoupon = async () => {
    const { data } = await axios.get(`${apiurl}/coupons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const found = data.find((c: Coupon) => c.coupon_id === Number(coupon_id));
    setCoupon(found || null);
  };

  const fetchRedemptions = async () => {
    const { data } = await axios.get(`${apiurl}/coupon/${coupon_id}/redemptions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setRedemptions(data);
  };

  useEffect(() => {
    if (!isCreating) {
      fetchCoupon();
      fetchRedemptions();
    }
  }, [coupon_id]);

  // ── Create mode ──
  const [creationMode, setCreationMode] = useState<"single" | "bulk">("single");

  const [singleForm, setSingleForm] = useState({
    coupon_code: "",
    description: "",
    discount_type: "PERCENTAGE",
    discount_value: "",
    start_date: "",
    end_date: "",
    max_redemptions: "",
    applicable_offer_id: "",
  });

  const [bulkForm, setBulkForm] = useState({
    prefix: "",
    count: "10",
    batch_label: "",
    discount_type: "PERCENTAGE",
    discount_value: "",
    start_date: "",
    end_date: "",
    max_redemptions_per_code: "1",
    applicable_offer_id: "",
  });

  const [generatedCodes, setGeneratedCodes] = useState<Coupon[]>([]);

  const handleSingleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSingleForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBulkChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBulkForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSingle = async () => {
    try {

      const start_date_utc = asUtcDate(singleForm.start_date);
      const end_date_utc = asUtcDate(singleForm.end_date);

      const payload: any = {
        coupon_code: singleForm.coupon_code.trim().toUpperCase(),
        description: singleForm.description || undefined,
        discount_type: singleForm.discount_type,
        discount_value: Number(singleForm.discount_value),
        start_date: toBackendISO(start_date_utc) || undefined,
        end_date: toBackendISO(end_date_utc) || undefined,
        max_redemptions: singleForm.max_redemptions ? Number(singleForm.max_redemptions) : undefined,
        applicable_offer_id: singleForm.applicable_offer_id ? Number(singleForm.applicable_offer_id) : undefined,
      };
      const { data } = await axios.post(`${apiurl}/coupon/create`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Coupon created", status: "success" });
      navigate(`/practice/app/b2c-coupon-manager/${data.coupon_id}/review-coupon`);
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error creating coupon", status: "error" });
    }
  };

  const handleCreateBulk = async () => {
    try {
      const start_date_utc = asUtcDate(bulkForm.start_date);
      const end_date_utc = asUtcDate(bulkForm.end_date);

      const payload: any = {
        prefix: bulkForm.prefix.trim().toUpperCase(),
        count: Number(bulkForm.count),
        batch_label: bulkForm.batch_label,
        discount_type: bulkForm.discount_type,
        discount_value: Number(bulkForm.discount_value),
        start_date: toBackendISO(start_date_utc) || undefined,
        end_date: toBackendISO(end_date_utc) || undefined,
        max_redemptions_per_code: Number(bulkForm.max_redemptions_per_code),
        applicable_offer_id: bulkForm.applicable_offer_id ? Number(bulkForm.applicable_offer_id) : undefined,
      };
      const { data } = await axios.post(`${apiurl}/coupon/bulk-create`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGeneratedCodes(data);
      toast({ title: `${data.length} coupon codes generated`, status: "success" });
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error generating coupons", status: "error" });
    }
  };

  const handleCopyAllCodes = () => {
    const codesText = generatedCodes.map(c => c.coupon_code).join("\n");
    navigator.clipboard.writeText(codesText);
    toast({ title: "Codes copied to clipboard", status: "success", duration: 2000 });
  };

  // ── VIEW EXISTING COUPON ──
  if (!isCreating) {
    if (!coupon) return null;


    return (
      <Box as="main" mx="auto" p={10}>
        <Box bg={pageBg} p={4} borderRadius="sm" mb={4}>
          <Flex justify="space-between" align="center" wrap="wrap">
            <Heading size="lg">{coupon.coupon_code}</Heading>
            <Badge colorScheme={coupon.is_active ? "green" : "gray"} fontSize="sm">
              {coupon.is_active ? "Active" : "Deactivated"}
            </Badge>
          </Flex>
        </Box>

        <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <Text mb={4} fontWeight="bold">Coupon Details</Text>
          <VStack align="stretch" spacing={3} fontSize="sm">
            <Flex justify="space-between"><Text>Batch / Description</Text><Text>{coupon.batch_label || coupon.description || "-"}</Text></Flex>
            <Flex justify="space-between"><Text>Discount</Text><Text>{coupon.discount_type === "PERCENTAGE" ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}</Text></Flex>
            {/* fmtLocal correctly parses the backend's naive-UTC string as
                UTC first, then renders it in the browser's local timezone --
                new Date(str).toLocaleString() was silently treating the
                stored UTC value as if it were already local time. */}
            <Flex justify="space-between"><Text>Valid From</Text><Text>{coupon.start_date ? fmtLocal(coupon.start_date) : "Anytime"}</Text></Flex>
            <Flex justify="space-between"><Text>Valid Until</Text><Text>{coupon.end_date ? fmtLocal(coupon.end_date) : "No expiry"}</Text></Flex>
            <Flex justify="space-between"><Text>Redemptions</Text><Text>{coupon.redemption_count}{coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : " / ∞"}</Text></Flex>
            <Flex justify="space-between"><Text>Applicable Offer</Text><Text>{coupon.applicable_offer_id || "Any offer"}</Text></Flex>
            <Flex justify="space-between"><Text>Created</Text><Text>{fmtLocal(coupon.created_at)}</Text></Flex>
          </VStack>
        </Box>

        <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <Text mb={4} fontWeight="bold">Redemption History</Text>
          {redemptions.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No redemptions yet.</Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Purchase ID</Th>
                  <Th>Account ID</Th>
                  <Th>Discount Applied</Th>
                  <Th>Redeemed At</Th>
                </Tr>
              </Thead>
              <Tbody>
                {redemptions.map((r) => (
                  <Tr key={r.coupon_redemption_id}>
                    <Td>{r.purchase_id}</Td>
                    <Td>{r.account_id}</Td>
                    <Td>{r.discount_amount}</Td>
                    <Td>{new Date(r.redeemed_at).toLocaleString()}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    );
  }

  // ── CREATE NEW COUPON(S) ──
  return (
    <Box as="main" mx="auto" p={10}>
      <Box bg={couponBg} p={4} borderRadius="sm" mb={4}>
        <Heading size="lg">New Coupon</Heading>
      </Box>

      <RadioGroup value={creationMode} onChange={(v) => setCreationMode(v as "single" | "bulk")} mb={4}>
        <Stack direction="row" spacing={6}>
          <Radio value="single">Single Coupon</Radio>
          <Radio value="bulk">Bulk Generate (e.g. for a school)</Radio>
        </Stack>
      </RadioGroup>

      {creationMode === "single" ? (
        <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Coupon Code</FormLabel>
              <Input name="coupon_code" placeholder="e.g. STMARYS2026" size="sm" value={singleForm.coupon_code} onChange={handleSingleChange} />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea name="description" size="sm" value={singleForm.description} onChange={handleSingleChange} />
            </FormControl>

            <HStack>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Discount Type</FormLabel>
                <Select name="discount_type" size="sm" value={singleForm.discount_type} onChange={handleSingleChange}>
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED_AMOUNT">Fixed Amount</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Discount Value</FormLabel>
                <Input name="discount_value" type="number" size="sm" value={singleForm.discount_value} onChange={handleSingleChange} />
              </FormControl>
            </HStack>

            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Start Date (optional)</FormLabel>
                <Input name="start_date" type="datetime-local" size="sm" value={singleForm.start_date} onChange={handleSingleChange} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">End Date (optional)</FormLabel>
                <Input name="end_date" type="datetime-local" size="sm" value={singleForm.end_date} onChange={handleSingleChange} />
              </FormControl>
            </HStack>

            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Max Redemptions (blank = unlimited)</FormLabel>
                <Input name="max_redemptions" type="number" size="sm" value={singleForm.max_redemptions} onChange={handleSingleChange} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Applicable Offer ID (blank = any offer)</FormLabel>
                <Input name="applicable_offer_id" type="number" size="sm" value={singleForm.applicable_offer_id} onChange={handleSingleChange} />
              </FormControl>
            </HStack>

            <Button colorScheme="teal" size="sm" onClick={handleCreateSingle} isDisabled={!singleForm.coupon_code || !singleForm.discount_value}>
              Create Coupon
            </Button>
          </VStack>
        </Box>
      ) : (
        <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Code Prefix</FormLabel>
              <Input name="prefix" placeholder="e.g. STMARYS" size="sm" value={bulkForm.prefix} onChange={handleBulkChange} />
              <Text fontSize="xs" color="gray.500" mt={1}>Generated codes look like STMARYS-A1B2C3</Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Batch Label</FormLabel>
              <Input name="batch_label" placeholder="e.g. St Mary's Selective VIC 2026" size="sm" value={bulkForm.batch_label} onChange={handleBulkChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Number of Codes</FormLabel>
              <Input name="count" type="number" size="sm" value={bulkForm.count} onChange={handleBulkChange} />
            </FormControl>

            <HStack>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Discount Type</FormLabel>
                <Select name="discount_type" size="sm" value={bulkForm.discount_type} onChange={handleBulkChange}>
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED_AMOUNT">Fixed Amount</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Discount Value</FormLabel>
                <Input name="discount_value" type="number" size="sm" value={bulkForm.discount_value} onChange={handleBulkChange} />
              </FormControl>
            </HStack>

            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Start Date (optional)</FormLabel>
                <Input name="start_date" type="datetime-local" size="sm" value={bulkForm.start_date} onChange={handleBulkChange} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">End Date (optional)</FormLabel>
                <Input name="end_date" type="datetime-local" size="sm" value={bulkForm.end_date} onChange={handleBulkChange} />
              </FormControl>
            </HStack>

            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Max Redemptions Per Code</FormLabel>
                <Input name="max_redemptions_per_code" type="number" size="sm" value={bulkForm.max_redemptions_per_code} onChange={handleBulkChange} />
                <Text fontSize="xs" color="gray.500" mt={1}>Usually 1 — one code, one student</Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Applicable Offer ID (blank = any offer)</FormLabel>
                <Input name="applicable_offer_id" type="number" size="sm" value={bulkForm.applicable_offer_id} onChange={handleBulkChange} />
              </FormControl>
            </HStack>

            <Button colorScheme="teal" size="sm" onClick={handleCreateBulk} isDisabled={!bulkForm.prefix || !bulkForm.batch_label || !bulkForm.discount_value}>
              Generate Codes
            </Button>

            {generatedCodes.length > 0 && (
              <Box mt={4}>
                <Divider mb={3} />
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="bold" fontSize="sm">{generatedCodes.length} Codes Generated</Text>
                  <Button size="xs" onClick={handleCopyAllCodes}>Copy All</Button>
                </Flex>
                <Box maxH="300px" overflowY="auto" bg={couponCodeBg} p={3} borderRadius="md" fontSize="sm" fontFamily="mono">
                  {generatedCodes.map((c) => (
                    <Text key={c.coupon_id}>{c.coupon_code}</Text>
                  ))}
                </Box>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default B2CReviewCoupon;